import { useMutation, UseMutationOptions, QueryKey, useQueryClient } from 'react-query'

type UseMutationWithOptimisticUpdateOptions<TQueryData, TData, TError, TVariables> =
  UseMutationOptions<TData, TError, TVariables, TQueryData | undefined> & {
    getQueryKey: (variables: TVariables) => QueryKey
    getUpdatedQueryData: (variables: TVariables, previousData: TQueryData | undefined) => TQueryData
  }

export const useMutationWithOptimisticUpdate = <TQueryData, TData = unknown, TError = Error, TVariables = void>(
  options: UseMutationWithOptimisticUpdateOptions<TQueryData, TData, TError, TVariables>
) => {
  const queryClient = useQueryClient()
  return useMutation({
    ...options,
    onMutate: async (variables: TVariables) => {
      options.onMutate?.(variables)
      const queryKey = options.getQueryKey(variables)
      await queryClient.cancelQueries({ queryKey })
      const previousData = queryClient.getQueryData<TQueryData>(queryKey)
      queryClient.setQueryData<TQueryData>(queryKey, options.getUpdatedQueryData(variables, previousData))
      return previousData
    },
    onError: (error, variables, context) => {
      options.onError?.(error, variables, context)
      if (context !== undefined) {
        queryClient.setQueryData<TQueryData>(options.getQueryKey(variables), context)
      }
    }
  })
}